import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Download, FileText, FileImage } from "lucide-react";

const ExportDropdown = ({
  song,
  onExportTxt,
  onExportPdf,
  isMobile = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // Close on scroll
  useEffect(() => {
    if (isOpen) {
      const handleScroll = () => setIsOpen(false);
      window.addEventListener("scroll", handleScroll, true);
      return () => window.removeEventListener("scroll", handleScroll, true);
    }
  }, [isOpen]);

  const toggleOpen = () => {
    if (isOpen) {
      setIsOpen(false);
      setMenuPos(null);
    } else {
      if (!isMobile && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPos({ top: rect.bottom + 4, left: rect.left });
      }
      setIsOpen(true);
    }
  };

  const handleExport = (type) => {
    if (type === "txt") {
      onExportTxt(song);
    } else if (type === "pdf") {
      onExportPdf(song);
    }
    setIsOpen(false);
  };

  const menuItems = [
    {
      key: "txt",
      label: "Text",
      Icon: FileText,
      onClick: () => handleExport("txt"),
    },
    {
      key: "pdf",
      label: "PDF",
      Icon: FileImage,
      onClick: () => handleExport("pdf"),
    },
  ];

  // Desktop dropdown (portal)
  if (!isMobile) {
    return (
      <div className="relative inline-flex">
        <button
          ref={buttonRef}
          onClick={toggleOpen}
          className="text-xs px-0.5 py-0.5 md:px-2 md:py-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-forest-200 inline-flex items-center gap-1"
          title="Export song"
        >
          <Download className="w-3 h-3 md:w-4 md:h-4 text-gold-500" />
          <span className="hidden md:inline">Export</span>
        </button>

        {isOpen &&
          menuPos &&
          createPortal(
            <div
              ref={dropdownRef}
              style={{
                position: "fixed",
                zIndex: 99999,
                top: `${menuPos.top}px`,
                left: `${menuPos.left}px`,
              }}
            >
              <div
                className="bg-forest-800 border border-forest-700 rounded-lg shadow-xl"
                style={{ display: "block", padding: "4px" }}
              >
                {menuItems.map((item) => (
                  <button
                    key={item.key}
                    onClick={item.onClick}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      width: "100%",
                      textAlign: "left",
                      fontSize: "12px",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      marginBottom: item.key !== "pdf" ? "2px" : "0",
                    }}
                    className="transition-colors bg-forest-700 hover:bg-forest-600 text-forest-200"
                  >
                    <item.Icon
                      style={{ width: "16px", height: "16px", flexShrink: 0 }}
                      className="text-gold-500"
                    />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>,
            document.body,
          )}
      </div>
    );
  }

  // Mobile slide-up panel (portal)
  return (
    <>
      <button
        ref={buttonRef}
        onClick={toggleOpen}
        className="text-xs px-0.5 py-0.5 md:px-2 md:py-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-forest-200 inline-flex items-center gap-1"
        title="Export song"
      >
        <Download className="w-3 h-3 md:w-4 md:h-4 text-gold-500" />
        <span className="hidden md:inline">Export</span>
      </button>

      {isOpen &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 99998,
                background: "rgba(0,0,0,0.3)",
              }}
              onClick={() => setIsOpen(false)}
            />
            {/* Slide-up panel above navbar */}
            <div
              ref={dropdownRef}
              style={{
                position: "fixed",
                bottom:
                  "max(84px, calc(68px + env(safe-area-inset-bottom, 0px)))",
                left: 0,
                right: 0,
                zIndex: 99999,
                animation: "exportSlideUp 0.2s ease-out",
              }}
            >
              <div
                className="bg-forest-800 border-t border-forest-700"
                style={{ display: "block", padding: "6px 8px" }}
              >
                {menuItems.map((item) => (
                  <button
                    key={item.key}
                    onClick={item.onClick}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      width: "100%",
                      fontSize: "14px",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      marginBottom: item.key !== "pdf" ? "4px" : "0",
                    }}
                    className="transition-colors bg-forest-700 hover:bg-forest-600 text-forest-200"
                  >
                    <item.Icon
                      style={{ width: "20px", height: "20px", flexShrink: 0 }}
                      className="text-gold-500"
                    />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <style>{`
              @keyframes exportSlideUp {
                from { transform: translateY(100%); }
                to { transform: translateY(0); }
              }
            `}</style>
          </>,
          document.body,
        )}
    </>
  );
};

export default ExportDropdown;
